<template>
  <div class="ob-col">
    <div class="ob-head">
      <span class="ob-head__icon" style="background: rgba(139, 92, 246, 0.12); color: #8b5cf6"><span class="mdi mdi-file-sign"></span></span>
      <h2 class="ob-head__title">Crea el contrato de tu inquilino</h2>
      <p class="ob-head__sub">Este contrato generará automáticamente los cargos de renta cada mes</p>
    </div>

    <div v-if="ob.contractsImported > 0" class="ob-readonly">
      <span class="mdi mdi-check-circle" style="color: #10b981"></span>
      <span>Ya importaste <b>{{ ob.contractsImported }}</b> {{ ob.contractsImported === 1 ? 'contrato' : 'contratos' }}. No necesitas crear uno aquí; continúa con el siguiente paso.</span>
    </div>

    <div v-else-if="ob.contractCreated" class="ob-readonly">
      <span class="mdi mdi-check-circle" style="color: #10b981"></span>
      <span>Contrato de <b>{{ ob.contractCreated.tenantName }}</b> en la nave {{ ob.contractCreated.plotName }} · {{ formatMoney(ob.contractCreated.rent) }} al mes. Ya está creado.</span>
    </div>

    <template v-if="ob.contractsImported === 0 && !ob.contractCreated">
      <div v-if="hasTenant" class="ob-readonly">
        <span class="mdi mdi-domain"></span>
        <span><b>{{ ob.tenant.name }}</b> · Nave {{ plotName }}</span>
      </div>
      <div v-else class="ob-hint-box" style="margin-top: 0; margin-bottom: 18px">
        Primero agrega a tu inquilino en el paso anterior. Si prefieres hacerlo después, usa "Omitir este paso" y créalo desde Contratos.
      </div>

      <div class="ob-row2">
        <div class="ob-field">
          <label class="ob-label" for="c-rent">Renta mensual</label>
          <div class="rent">
            <input id="c-rent" v-model="rent" class="ob-input ob-input--mono rent__input" inputmode="decimal" placeholder="0.00" :disabled="!hasTenant" />
            <span class="rent__currency" title="Por ahora los contratos se manejan en pesos mexicanos">MXN</span>
          </div>
        </div>
        <div class="ob-field">
          <label class="ob-label" for="c-start">Fecha de inicio</label>
          <input id="c-start" v-model="ob.contract.startDate" type="date" class="ob-input" :disabled="!hasTenant" />
        </div>
        <div class="ob-field">
          <label class="ob-label" for="c-end">Fecha de vencimiento</label>
          <input id="c-end" v-model="ob.contract.endDate" type="date" class="ob-input" :min="ob.contract.startDate" :disabled="!hasTenant" />
        </div>
      </div>

      <label class="toggle">
        <input v-model="ob.contract.escalate" type="checkbox" :disabled="!hasTenant" />
        <span class="toggle__track"><span class="toggle__thumb"></span></span>
        <span class="toggle__text">Contrato con escalación anual</span>
      </label>
      <div v-if="ob.contract.escalate" class="stepper">
        <button type="button" class="stepper__btn" aria-label="Disminuir" @click="bump(-0.5)">−</button>
        <span class="stepper__value ob-input--mono">{{ ob.contract.escalationPercent }}%</span>
        <button type="button" class="stepper__btn" aria-label="Aumentar" @click="bump(0.5)">＋</button>
        <span class="ob-note" style="margin: 0">La renta sube este porcentaje cada aniversario del contrato.</span>
      </div>

      <div class="ob-field">
        <label class="ob-label" for="c-opening">Saldo pendiente anterior a Parkos (opcional)</label>
        <div class="rent">
          <input id="c-opening" v-model="opening" class="ob-input ob-input--mono rent__input" inputmode="decimal" placeholder="0.00" :disabled="!hasTenant" />
          <span class="rent__currency">MXN</span>
        </div>
        <p class="ob-note" style="margin: 4px 0 0">Si este inquilino ya te debía algo antes de usar Parkos, se registra como un solo cargo vencido.</p>
      </div>

      <p class="live">{{ liveLine }}</p>
    </template>

    <p v-if="ob.error" class="ob-error">{{ ob.error }}</p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { ob } from '@/services/onboardingStore'
import { formatMoney, formatLongDate, todayYmd } from './onboardingFormat'

const hasTenant = computed(() => !!(ob.tenant.name.trim() && ob.tenant.plotId))
const plotName = computed(() => ob.plots.find((p) => p.id === ob.tenant.plotId)?.name || '')

const rent = computed({
  get: () => ob.contract.rent,
  set: (v) => { ob.contract.rent = String(v).replace(/[^0-9.]/g, '') },
})

const opening = computed({
  get: () => ob.contract.openingBalance,
  set: (v) => { ob.contract.openingBalance = String(v).replace(/[^0-9.]/g, '') },
})

function bump(delta) {
  const next = Math.round((Number(ob.contract.escalationPercent) + delta) * 10) / 10
  ob.contract.escalationPercent = Math.min(30, Math.max(0.5, next))
}

// Conecta la configuración con la consecuencia: cuánto y cuándo se cobra.
const liveLine = computed(() => {
  const amount = Number(ob.contract.rent)
  if (!(amount > 0) || !ob.contract.startDate) return 'Escribe la renta para ver cuándo se generará el primer cargo.'
  const past = ob.contract.startDate < todayYmd()
  const tail = past ? ' Si el contrato ya empezó, en el paso de cobranza eliges si Parkos incluye los meses anteriores.' : ''
  return `Este contrato generará un cargo de ${formatMoney(amount)} el día ${ob.billing.rentDueDay} de cada mes a partir del ${formatLongDate(ob.contract.startDate)}.${tail}`
})
</script>

<style scoped>
.rent { display: flex; gap: 8px; }
.rent__input { font-size: 16px; flex: 1; min-width: 0; }
.rent__currency { height: 42px; padding: 0 12px; display: inline-flex; align-items: center; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; font: 500 12px var(--font-mono); color: #64748b; cursor: help; }

.toggle { display: inline-flex; align-items: center; gap: 10px; cursor: pointer; margin: 4px 0 10px; }
.toggle input { position: absolute; opacity: 0; pointer-events: none; }
.toggle__track { width: 38px; height: 22px; border-radius: 11px; background: #cbd5e1; position: relative; transition: background 0.15s; flex-shrink: 0; }
.toggle__thumb { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform 0.15s; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); }
.toggle input:checked + .toggle__track { background: #3b82f6; }
.toggle input:checked + .toggle__track .toggle__thumb { transform: translateX(16px); }
.toggle input:focus-visible + .toggle__track { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25); }
.toggle input:disabled + .toggle__track { opacity: 0.5; }
.toggle__text { font: 400 13px var(--font-body); color: #1f2937; }

.stepper { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
.stepper__btn { width: 32px; height: 32px; border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; cursor: pointer; font-size: 16px; color: #1e3a5f; }
.stepper__btn:hover { background: #f1f5f9; }
.stepper__value { min-width: 56px; text-align: center; font-size: 15px; font-weight: 500; color: #1e3a5f; }

.live { margin: 20px 0 0; font: 400 12px/1.5 var(--font-body); color: #3b82f6; }
</style>